import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiUrl } from '../../config/config';

const ToolApparelContext = createContext(null);

export const ToolApparelProvider = ({ children }) => {
  const [records, setRecords] = useState({});
  const requests = useRef(new Map());

  const load = useCallback((id) => {
    if (requests.current.has(id)) return requests.current.get(id);
    // Cache the promise immediately so simultaneous consumers and StrictMode
    // effects share the same request, including while it is still pending.
    const request = Promise.resolve().then(async () => {
      try {
        const response = await fetch(`${apiUrl}/api/tool-apparels/${encodeURIComponent(id)}`);
        if (!response.ok) throw new Error(`Unable to load apparel (${response.status})`);
        const data = await response.json();
        setRecords((previous) => ({ ...previous, [id]: { data, error: null } }));
      } catch (error) {
        setRecords((previous) => ({ ...previous, [id]: { data: null, error } }));
      }
    });
    requests.current.set(id, request);
    return request;
  }, []);

  const retry = useCallback((id) => {
    requests.current.delete(id);
    setRecords((previous) => ({ ...previous, [id]: undefined }));
    return load(id);
  }, [load]);

  const value = useMemo(() => ({ records, load, retry }), [records, load, retry]);
  return <ToolApparelContext.Provider value={value}>{children}</ToolApparelContext.Provider>;
};

export const useToolApparel = (apparelId) => {
  const context = useContext(ToolApparelContext);
  if (!context) throw new Error('useToolApparel must be used within ToolApparelProvider');
  const id = apparelId == null ? '' : String(apparelId);
  const { records, load, retry } = context;
  useEffect(() => {
    if (id) load(id);
  }, [id, load]);
  const record = records[id];
  return {
    data: record?.data ?? null,
    error: record?.error ?? null,
    isLoading: Boolean(id) && !record,
    retry: () => id && retry(id),
  };
};
